import { useCallback } from 'react';
import { useUser } from './useUser';
import { useDispatch, useSelector } from 'react-redux';
import {
  setIsLoading,
  setParsedLogs,
  setError,
} from '../utils/redux/slices/serverLogsSlice';
import { RootState } from '../utils/redux';

export const useServerLogsData = (callSid?: string, agentId?: string) => {
  const { user } = useUser();
  const dispatch = useDispatch();
  const state = useSelector((state: RootState) => state.serverLogs);

  // S3 URL for server logs
  const s3Url = `s3://quibble-client/${user?.tenantName}/${agentId}/audio/server-call-logs/${callSid}.jsonl`;

  // Convert S3 URL to HTTPS URL
  const getHttpsUrl = (s3Url: string): string => {
    const regex = new RegExp('s3://([^/]+)/(.+)');
    const match = regex.exec(s3Url);
    if (match) {
      const [, bucket, key] = match;
      return `https://${bucket}.s3.amazonaws.com/${key}`;
    }
    return s3Url;
  };

  const fetchLogData = useCallback(async () => {
    dispatch(setIsLoading(true));
    dispatch(setError(null));

    try {
      const httpsUrl = getHttpsUrl(s3Url);
      const response = await fetch(`${httpsUrl}?t=${Date.now()}`, {
        cache: 'no-cache',
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch: ${response.status} ${response.statusText}`
        );
      }

      // Handle as binary data first
      const arrayBuffer = await response.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);

      // Convert to text, handling potential encoding issues
      const decoder = new TextDecoder('utf-8');
      const textData = decoder.decode(uint8Array);

      // Parse JSONL data - each line should be a valid JSON object
      const lines = textData.trim().split('\n');
      const parsed = lines
        .filter(line => line.trim())
        .map((line, lineIndex) => {
          try {
            return JSON.parse(line);
          } catch {
            console.warn(`Failed to parse JSON line ${lineIndex + 1}:`, line);
            return null;
          }
        })
        .filter(item => item !== null);

      dispatch(setParsedLogs(parsed));
    } catch (err) {
      console.error('Error fetching log data:', err);
      dispatch(
        setError(
          err instanceof Error ? err.message : 'Failed to fetch log data'
        )
      );
    } finally {
      dispatch(setIsLoading(false));
    }
  }, [s3Url]);

  return {
    fetchLogData: fetchLogData,
    parsedLogs: state.parsedLogs,
    isLoading: state.isLoading,
    error: state.error,
  };
};

export default useServerLogsData;
